/**
 * Store global de alertas.
 *
 * Fonte única do feedback transiente do app: `AlertaHost` renderiza o
 * `AlertaReutilizavel` a partir desta fila e os demais módulos publicam usando
 * `alerta.*` (fora de componentes) ou `useAlerta` (dentro de componentes).
 *
 * Sem Context/Provider: `useSyncExternalStore` mantém o mesmo estado em toda a
 * árvore e continua funcionando em testes que renderizam componentes isolados.
 */

const LIMITE_FILA = 5;
const TIPOS = ['success', 'error', 'warning', 'info'];

let sequencia = 0;
let estado = { fila: [] };
const ouvintes = new Set();

const notificarOuvintes = () => {
  ouvintes.forEach((ouvinte) => ouvinte());
};

const normalizarTipo = (tipo) => (TIPOS.includes(tipo) ? tipo : 'info');

const gerarId = () => {
  sequencia += 1;
  return `alerta-${Date.now()}-${sequencia}`;
};

const criarAlerta = (mensagem, tipo, opcoes = {}) => ({
  id: opcoes.id || gerarId(),
  mensagem,
  tipo: normalizarTipo(tipo),
  titulo: opcoes.titulo || null,
  autoHideDuration: opcoes.autoHideDuration,
  posicao: opcoes.posicao,
  variant: opcoes.variant,
  acao: opcoes.acao || null,
});

export const getSnapshot = () => estado;

export const subscribe = (ouvinte) => {
  ouvintes.add(ouvinte);

  return () => ouvintes.delete(ouvinte);
};

/** Enfileira um alerta (ignora mensagens vazias e repetições imediatas). */
export const pushAlerta = (mensagem, tipo = 'info', opcoes = {}) => {
  if (!mensagem) return null;

  const tipoNormalizado = normalizarTipo(tipo);
  const ultimo = estado.fila[estado.fila.length - 1];

  if (ultimo && ultimo.mensagem === mensagem && ultimo.tipo === tipoNormalizado) {
    return ultimo.id;
  }

  const alertaNovo = criarAlerta(mensagem, tipoNormalizado, opcoes);

  estado = { fila: [...estado.fila, alertaNovo].slice(-LIMITE_FILA) };
  notificarOuvintes();

  return alertaNovo.id;
};

/** Fecha o alerta atual (sem id) ou um alerta específico. */
export const fecharAlerta = (id = null) => {
  if (estado.fila.length === 0) return;

  const fila = id ? estado.fila.filter((item) => item.id !== id) : estado.fila.slice(1);
  if (fila.length === estado.fila.length) return;

  estado = { fila };
  notificarOuvintes();
};

/** Remove todos os alertas da fila. */
export const limparAlertas = () => {
  if (estado.fila.length === 0) return;

  estado = { fila: [] };
  notificarOuvintes();
};

/** Substitui a fila por um único alerta (compatibilidade com `setAlerta`). */
export const substituirAlerta = (mensagem, tipo = 'info', opcoes = {}) => {
  if (!mensagem) {
    limparAlertas();
    return null;
  }

  const alertaNovo = criarAlerta(mensagem, tipo, opcoes);

  estado = { fila: [alertaNovo] };
  notificarOuvintes();

  return alertaNovo.id;
};

/**
 * API imperativa para módulos fora de componentes (ou que não precisam de
 * re-render). Sempre desemboca no mesmo `AlertaReutilizavel` via `AlertaHost`.
 */
export const alerta = Object.freeze({
  show: (mensagem, tipo = 'info', opcoes = {}) => pushAlerta(mensagem, tipo, opcoes),
  success: (mensagem, opcoes = {}) => pushAlerta(mensagem, 'success', opcoes),
  error: (mensagem, opcoes = {}) => pushAlerta(mensagem, 'error', opcoes),
  warning: (mensagem, opcoes = {}) => pushAlerta(mensagem, 'warning', opcoes),
  info: (mensagem, opcoes = {}) => pushAlerta(mensagem, 'info', opcoes),
  dismiss: () => fecharAlerta(),
  clear: () => limparAlertas(),
});
